import React from "react";
import { Link, Typography } from "@mui/material";
import LegalPageLayout, { LegalSection } from "../components/LegalPageLayout";

const TermsPage: React.FC = () => (
  <LegalPageLayout
    title="Terms of Use"
    description="These Terms govern your use of raymondjones.dev and your interactions with Ray Jones’s applications, content, coaching, communities, and digital offerings."
  >
    <LegalSection title="1. Acceptance and eligibility">
      <Typography component="p">
        By accessing the site, entering information, clicking an acceptance box,
        submitting an application, scheduling a call, making a purchase, or using an
        offering, you agree to these Terms and the Privacy Policy. If you do not
        agree, do not use the site. You must be at least 18 years old and legally able
        to enter a binding agreement.
      </Typography>
    </LegalSection>

    <LegalSection title="2. Offerings and application decisions">
      <Typography component="p">
        The site may provide educational content, communities, digital products,
        coaching, application reviews, calls, and related services (“Offerings”).
        Descriptions, availability, eligibility, pricing, and features may change.
        Unless prohibited by law, Ray may accept, decline, prioritize, close, or
        discontinue an application or Offering at his discretion and is not required
        to provide a reason.
      </Typography>
      <Typography component="p">
        You must provide accurate, current information and promptly correct material
        errors. Submitting an application does not create a coaching relationship,
        guarantee admission, reserve availability, or obligate Ray to provide
        services.
      </Typography>
    </LegalSection>

    <LegalSection title="3. Communications consent">
      <Typography component="p">
        When you provide contact information and accept the application disclosure,
        you agree that Ray may contact you by email or text about your application,
        booking, requested information, and related services. Message frequency
        varies; message and data rates may apply. Reply STOP to opt out of texts and
        use an unsubscribe link where provided. Consent to promotional marketing is
        not a condition of purchase. You remain responsible for carrier, internet,
        and device charges.
      </Typography>
    </LegalSection>

    <LegalSection title="4. Payments, refunds, and chargebacks">
      <Typography component="p">
        Prices, payment schedules, included services, and any refund terms shown at
        checkout or in a signed agreement are incorporated into these Terms. Except
        where a specific written refund policy or applicable law requires otherwise,
        purchases are final once digital access or services begin. You authorize the
        stated charges and agree not to initiate a chargeback for a valid charge
        without first giving Ray a reasonable opportunity to resolve the issue.
      </Typography>
      <Typography component="p">
        Taxes and third-party fees may apply. Late, failed, reversed, or disputed
        payments may result in suspended access, collection costs permitted by law,
        and withholding of undelivered services.
      </Typography>
    </LegalSection>

    <LegalSection title="5. Educational nature; no guarantees">
      <Typography component="p">
        The Offerings provide education, strategy, feedback, and support—not legal,
        tax, financial, medical, immigration, recruiting, or employment advice. You
        are responsible for your decisions, preparation, applications, interviews,
        negotiations, and compliance obligations.
      </Typography>
      <Typography component="p">
        Testimonials and examples describe individual experiences and are not typical
        or guaranteed. Ray does not guarantee interviews, employment, compensation,
        promotions, admissions, revenue, or any particular result. Outcomes depend
        on factors outside Ray’s control, including your background, effort, market
        conditions, employers, and third-party decisions.
      </Typography>
    </LegalSection>

    <LegalSection title="6. Intellectual property and limited license">
      <Typography component="p">
        The site and Offerings—including videos, frameworks, curricula, templates,
        text, graphics, code, branding, and recordings—are owned by Ray or licensed
        to him and protected by intellectual-property laws. Subject to payment and
        compliance with these Terms, you receive a limited, personal,
        non-exclusive, non-transferable, revocable license to use purchased or
        provided materials for your own learning.
      </Typography>
      <Typography component="p">
        You may not copy, record, scrape, republish, sell, sublicense, distribute,
        share credentials, create competing derivative products, remove notices, or
        use materials to train a model or commercial service without prior written
        permission.
      </Typography>
    </LegalSection>

    <LegalSection title="7. Acceptable use and community conduct">
      <Typography component="p">
        You may not misuse the site or Offerings; impersonate others; submit false,
        malicious, infringing, unlawful, or harassing material; bypass access
        controls; disrupt systems; harvest data; spam; or violate another person’s
        rights. Ray may remove content or suspend or terminate access for suspected
        misuse, nonpayment, risk to others, or breach of these Terms, subject to
        applicable law.
      </Typography>
    </LegalSection>

    <LegalSection title="8. Third-party services">
      <Typography component="p">
        The site may link to or use third-party services such as Cal.com, Stripe,
        Airtable, Resend, Loom, Meta, Vercel, and Skool. Ray does not control and is
        not responsible for third-party availability, content, security, fees,
        decisions, or practices. Your use of those services may be governed by their
        separate terms and privacy policies.
      </Typography>
    </LegalSection>

    <LegalSection title="9. Disclaimers">
      <Typography component="p">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SITE AND OFFERINGS ARE PROVIDED
        “AS IS” AND “AS AVAILABLE.” RAY DISCLAIMS ALL EXPRESS, IMPLIED, AND STATUTORY
        WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        TITLE, NON-INFRINGEMENT, ACCURACY, AVAILABILITY, AND RESULTS. RAY DOES NOT
        WARRANT UNINTERRUPTED, SECURE, OR ERROR-FREE OPERATION.
      </Typography>
    </LegalSection>

    <LegalSection title="10. Limitation of liability">
      <Typography component="p">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, RAY AND HIS SERVICE PROVIDERS WILL
        NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, CONSEQUENTIAL,
        OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, REVENUE, DATA, GOODWILL, EMPLOYMENT,
        OR BUSINESS OPPORTUNITY. TOTAL LIABILITY ARISING FROM THE SITE, AN OFFERING,
        OR THESE TERMS WILL NOT EXCEED THE GREATER OF US $100 OR THE AMOUNT YOU PAID
        RAY FOR THE APPLICABLE OFFERING DURING THE 12 MONTHS BEFORE THE EVENT GIVING
        RISE TO THE CLAIM.
      </Typography>
      <Typography component="p">
        These exclusions do not apply to liability that cannot lawfully be excluded
        or limited.
      </Typography>
    </LegalSection>

    <LegalSection title="11. Indemnification">
      <Typography component="p">
        To the extent permitted by law, you will defend, indemnify, and hold harmless
        Ray and his contractors and service providers from third-party claims,
        damages, losses, and reasonable costs arising from your unlawful conduct,
        misuse of the site or Offerings, content you submit, infringement of another
        person’s rights, or material breach of these Terms.
      </Typography>
    </LegalSection>

    <LegalSection title="12. Governing law and disputes">
      <Typography component="p">
        These Terms are governed by California law, without regard to conflict-of-law
        rules. To the extent legally permitted, disputes must be brought in the state
        or federal courts located in Los Angeles County, California, and each party
        consents to their jurisdiction. Mandatory consumer protections and
        non-waivable rights in your home jurisdiction remain unaffected.
      </Typography>
    </LegalSection>

    <LegalSection title="13. General terms">
      <Typography component="p">
        These Terms, the Privacy Policy, checkout terms, and any signed agreement are
        the entire agreement for their subject matter. More specific signed or
        checkout terms control if they conflict with these Terms. If a provision is
        unenforceable, it will be narrowed to the minimum extent necessary and the
        remainder will continue. Failure to enforce a provision is not a waiver.
        You may not assign your rights without written consent; Ray may assign these
        Terms as part of a business transaction. Ray is not liable for delay caused
        by events beyond reasonable control.
      </Typography>
    </LegalSection>

    <LegalSection title="14. Changes, termination, and contact">
      <Typography component="p">
        Ray may update these Terms by posting a revised version. Material changes
        will receive additional notice where required. Continued use after the
        effective date constitutes acceptance to the extent permitted by law. Terms
        that by their nature should survive termination—including payment,
        intellectual property, disclaimers, liability limits, indemnity, and dispute
        provisions—will survive.
      </Typography>
      <Typography component="p">
        Questions may be sent to{" "}
        <Link href="mailto:raymond@raymondjones.dev">
          raymond@raymondjones.dev
        </Link>
        .
      </Typography>
    </LegalSection>
  </LegalPageLayout>
);

export default TermsPage;
